<template>
  <div
    class="purchasable-item-sales"
    v-if="purchasableItem"
  >
    <purchasable-item-nav
      :purchasableItemId="purchasableItemId"
    />

    <h2
    >
      {{ purchasableItem.name }} Purchases
    </h2>

    <p
      class="alert alert-info text-center"
      role="alert"
    >
      All purchase times are converted to <a href="https://www.timeanddate.com/time/aboututc.html" target="_blank">UTC</a>.

      Consequently, a purchase placed at 1AM CST on June 2nd will be graphed as a June 1st purchase.
    </p>

    <div class="row">
      <div class="col">
        <label
          for="start_at"
        >
          Start At
        </label>

        <input
          type="date"
          name="purchases_start_at"
          v-model="startAtDate"
          class="form-control"
        />
      </div>
      <div class="col">
        <label
          for="end_at"
        >
          End At
        </label>

        <input
          type="date"
          name="end_at"
          id="purchases_end_at"
          v-model="endAtDate"
          class="form-control"
        />
      </div>
      <div class="col pt-4">
        <button
          type="submit"
          class="btn btn-primary btn-block"
          v-on:click.prevent="getPurchasesByRange"
        >
          Get Purchases
          <spinner size="small" v-if="loading" />
        </button>
      </div>
    </div>

    <div
      :id="chartId"
    ></div>

    <pagination
      ref="pagination"
      :pageResponse="pageResponse"
      :resourceUrl="purchasesUrl"
      method="post"
      :formdata="purchasesFormdata"
      orderBy="purchased_at"
      orderType="DESC"
    >
      <template
        slot="paged_item"
        slot-scope="item"
      >
        <selected-purchases-list
          :purchase="item"
          no-key
          show-buyer
        />
      </template>
    </pagination>
  </div>
</template>
<script>
import Pagination from 'sai-vue-kaminari'

import PurchaseListItem from '@/components/PurchaseListItem'
import PurchasableItemNav from '@/components/PurchasableItemNav'

import Spinner from 'vue-simple-spinner'

export default {
  data () {
    return {
      chart: undefined,
      chartId: 'purchases-chart',
      loading: false,
      pageResponse: {},
      purchasesCounts: 0,
      purchases: undefined,
      purchasableItem: {},
      purchasesStartAt: undefined,
      purchasesEndAt: undefined,
      selectedPurchases: [],
      startAtDate: undefined,
      endAtDate: undefined
    }
  },

  props: {
    purchasableItemId: {
      type: [Number, String],
      default: undefined
    },

    startAt: {
      type: [Date, String],
      default () {
        let d = new Date()
        return new Date(d.getFullYear(), d.getMonth() - 1, 1)
      }
    },

    endAt: {
      type: [Date, String],
      default () {
        let d = new Date()
        d.setDate(d.getDate() + 1)

        return d
      }
    }
  },

  computed: {
    chartHeader () {
      return [
        {
          type: 'string',
          label: 'Day'
        },
        {
          type: 'number',
          label: 'Sales'
        }
      ]
    },

    dateRange () {
      let dates = {}
      let currDate = new Date(this.startAtDate)
      let endDate = new Date(this.endAtDate)

      while (new Date(currDate) <= new Date(endDate)) {
        // Change purchase datetime to UTC.
        currDate.setUTCHours(12)
        let d = new Date(currDate.toUTCString())
        let dateKey = d.toDateString()
        dates[dateKey] = 0
        currDate.setDate(d.getUTCDate() + 1)
      }

      return dates
    },

    purchasesFormdata () {
      return {
        start_at: this.startAtDate,
        end_at: this.endAtDate
      }
    },

    purchasableItemUrl () {
      return '/purchasable_items/' + this.purchasableItemId
    },

    purchasesUrl () {
      return this.apiUrl(
        this.purchasableItemUrl + '/purchases_by_date_range',
        true
      )
    },

    purchasesByDateUrl () {
      return this.apiUrl(
        this.purchasableItemUrl + '/purchases_by_date',
        true
      )
    }
  },

  components: {
    'pagination': Pagination,
    'selected-purchases-list': PurchaseListItem,
    'purchasable-item-nav': PurchasableItemNav,
    'spinner': Spinner
  },

  created () {
    this.apiGet(this.purchasableItemUrl).then((item) => {
      this.purchasableItem = item

      this.purchasesStartAt = this.startAt
      this.purchasesEndAt = this.endAt
      this.endAtDate = this.$root.dateFmt(this.purchasesEndAt)
      this.startAtDate = this.$root.dateFmt(this.purchasesStartAt)

      this.getPurchasesByRange()
    })
  },

  methods: {
    onPageSuccess (resp) {
      this.purchases = resp.body.data

      this.chartData = [ this.chartHeader ]
      this.purchasesCounts = this.processPurchases(resp)

      window.google.charts.load('current', { 'packages': ['corechart', 'line'] })
      window.google.charts.setOnLoadCallback(this.drawChart)
    },

    processPagedResponse (resp) {
      this.$emit('processPagedResponse', resp)
    },

    drawChart () {
      var data = new window.google.visualization.DataTable()
      data.addColumn('string', 'Days')
      data.addColumn('number', 'Sales')

      for (let date in this.purchasesCounts) {
        let d = new Date(date)
        let day = [
          this.$root.months[d.getUTCMonth()].substr(0, 3),
          d.getUTCDate(),
          d.getUTCFullYear().toString().substr(2, 3)
        ].join(' ')

        this.chartData.push([day, this.purchasesCounts[date]])
      }

      data.addRows(this.chartData)

      var options = {
        chartArea: {
          top: 45,
          height: '50%'
        },
        hAxis: {
        },
        vAxis: {
          title: 'Sales'
        }
      }

      var chart = new window.google.visualization.LineChart(document.getElementById(this.chartId))

      window.google.visualization.events.addListener(chart, 'select', () => {
        this.selectHandler(chart, data)
      })

      chart.draw(data, options)
    },

    selectHandler (chart, data) {
      var selectedItem = chart.getSelection()[0]
      var value = data.getValue(selectedItem.row, 0)

      this.selectedPurchases = []
      this.getPurchasesByDate(value).then((items) => {
        this.selectedPurchases = items
      })
    },

    getPurchasesByDate (date) {
      this.loading = true

      return this.$http.post(
        this.purchasesByDateUrl, {
          date: this.$root.dateFmt(date)
        }
      ).then((resp) => {
        this.loading = false
        return resp.body.data
      })
    },

    getPurchasesByRange () {
      this.loading = true

      return this.$http.post(
        this.purchasesUrl, this.purchasesFormdata
      ).then((resp) => {
        this.loading = false
        this.pageResponse = resp
      }).finally(() => { this.loading = false })
    },

    processPurchases (resp) {
      // @todo should optimize this server-side.
      let dates = this.dateRange

      this.purchases = resp.body.data
      this.purchases.map((purchase) => {
        const d = new Date(purchase.attributes.purchased_at)
        dates[d.toDateString()] += 1
      })

      return dates
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/PurchasableItemPurchases.vue